// src/components/GoldilocksZones.tsx
// ✅ FIXED: Goldilocks ROI zones component with correct increase/decrease logic

import React from 'react';
import { ReadinessZone } from '../types';
import { BusinessIntelligenceData } from '../../../services/businessIntelligenceService';
import HelpTooltip from '../../../shared/components/HelpTooltip';
import { getReadinessBandLabel, getReadinessBandStyle, formatOptimizationAction, getOptimalRangeLabel } from '../../../utils/bands';

interface GoldilocksZonesProps {
  readinessZones: ReadinessZone[];
  businessData: BusinessIntelligenceData | null;
  asIsScores?: { [key: string]: number }; // 🎯 NEW: Show baseline scores
  toBeScores?: { [key: string]: number }; // 🎯 NEW: Show projected scores
  isSimulating?: boolean; // 🎯 NEW: Show dual display when simulating
  title?: string;
}

const GoldilocksZones: React.FC<GoldilocksZonesProps> = ({ 
  readinessZones, 
  businessData,
  asIsScores,
  toBeScores,
  isSimulating = false,
  title = "💰 Goldilocks ROI - Optimal Readiness Zones" 
}) => {
  // 🎯 NEW: Helper function to get score display info
  const getScoreDisplayInfo = (zoneName: string, zoneCurrentScore: number) => {
    const categoryKey = zoneName.toLowerCase().replace(/[^a-z]/g, '_').replace('_+', '_').replace(/^_|_$/g, '');
    
    // Try different category key variations
    const possibleKeys = [
      categoryKey,
      categoryKey.replace('technical_debt', 'technicalDebt').replace('third_party', 'thirdParty'),
      zoneName.toLowerCase().replace(/\s+/g, '_'),
      zoneName.toLowerCase().replace(/\s+/g, '')
    ];
    
    let asIsValue = zoneCurrentScore;
    let toBeValue = zoneCurrentScore;
    
    // Find matching key in score objects
    for (const key of possibleKeys) {
      if (asIsScores && asIsScores[key] !== undefined) {
        asIsValue = asIsScores[key];
      }
      if (toBeScores && toBeScores[key] !== undefined) {
        toBeValue = toBeScores[key];
      }
    }
    
    return { asIs: asIsValue, toBe: toBeValue, hasChange: Math.abs(asIsValue - toBeValue) > 0.1 };
  };

  // ✅ FIXED: Helper function to get correct action and target value
  const getOptimizationAction = (zone: ReadinessZone) => {
    if (zone.current < zone.optimalMin) {
      return {
        action: 'increase',
        target: zone.optimalMin,
        needsOptimization: true
      };
    } else if (zone.current > zone.optimalMax) {
      return {
        action: 'decrease', // ✅ FIXED: This will now work for Third Party
        target: zone.optimalMax,
        needsOptimization: true
      };
    } else {
      return {
        action: 'maintain',
        target: zone.current,
        needsOptimization: false
      };
    }
  };

  // Calculate total friction and overall efficiency
  // All zones share one company's revenue measurement, so this is all-or-nothing —
  // but check with .some() rather than assume, since that's an implementation detail
  // of DefenseModule's calculateReadinessZones, not a contract this component owns.
  const frictionUnmeasured = readinessZones.some(zone => zone.frictionUnmeasured === true);
  const totalFrictionCost = readinessZones.reduce((total, zone) => total + zone.frictionAmount, 0);
  const overallEfficiency = Math.round(readinessZones.reduce((total, zone) => {
    const efficiency = zone.isInOptimal ? 100 : Math.max(50, 100 - (zone.frictionAmount / 10000));
    return total + efficiency;
  }, 0) / readinessZones.length);

  // Helper function to get zone icon
  const getZoneIcon = (zone: ReadinessZone): string => {
    if (zone.isInOptimal) return '✅';
    const optimization = getOptimizationAction(zone);
    if (optimization.action === 'decrease') {
      return '📉'; // Down arrow for decrease
    } else if (optimization.action === 'increase') {
      return '📈'; // Up arrow for increase
    } else {
      return '⚠️';
    }
  };

  // ✅ FIXED: Get top optimization opportunities with correct logic
  const topOpportunities = readinessZones
    .filter(zone => !zone.isInOptimal)
    .map(zone => ({
      ...zone,
      optimization: getOptimizationAction(zone)
    }))
    .sort((a, b) => b.frictionAmount - a.frictionAmount)
    .slice(0, 3);

  return (
    <div style={{ 
      padding: '20px',
      backgroundColor: 'var(--ra-glass-bg)',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      marginBottom: '20px'
    }}>
      <h3 style={{ color: 'var(--ra-text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {title} <HelpTooltip contentKey="defense.goldilocks" />
      </h3>

      {/* Overall Efficiency and Business Friction Dashboard */}
      <div style={{ 
        padding: '20px', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        color: 'white',
        marginBottom: '20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative icons */}
        <div style={{ position: 'absolute', top: '10px', left: '10px', fontSize: '24px', opacity: 0.3 }}>🎯</div>
        <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '24px', opacity: 0.3 }}>💎</div>
        <div style={{ position: 'absolute', bottom: '10px', left: '10px', fontSize: '24px', opacity: 0.3 }}>📊</div>
        <div style={{ position: 'absolute', bottom: '10px', right: '10px', fontSize: '24px', opacity: 0.3 }}>⚡</div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Overall Efficiency:</div>
            {frictionUnmeasured ? (
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '4px' }}>
                Not measured
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  height: '20px',
                  width: '200px',
                  position: 'relative'
                }}>
                  <div style={{
                    background: 'linear-gradient(90deg, #ffd700, #90EE90)',
                    height: '100%',
                    width: `${overallEfficiency}%`,
                    borderRadius: '10px',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{getReadinessBandLabel(overallEfficiency)}</span>
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Business Friction:</div>
            {frictionUnmeasured ? (
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e6f1ff' }}>
                Not measured
              </div>
            ) : (
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: totalFrictionCost > 300000 ? '#ffeb3b' : '#90EE90'
              }}>
                ${totalFrictionCost.toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* High friction warning */}
        {!frictionUnmeasured && totalFrictionCost > 300000 && (
          <div style={{ 
            backgroundColor: 'rgba(255,235,59,0.2)', 
            border: '1px solid rgba(255,235,59,0.5)',
            borderRadius: '8px',
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <div>
              <strong>High Business Friction Detected!</strong> ${totalFrictionCost.toLocaleString()} in annual waste due to readiness misalignment.
            </div>
          </div>
        )}

        {/* ✅ FIXED: Top optimization opportunities with correct action logic */}
        {/* Ranking by frictionAmount is meaningless when revenue is unmeasured — every
            zone ties at 0, so the "top" 3 would be an arbitrary, confident-looking list. */}
        {!frictionUnmeasured && topOpportunities.length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
              🎯 Top Optimization Opportunities:
            </div>
            {topOpportunities.map((zone, index) => (
              <div key={zone.name} style={{ 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                borderRadius: '6px',
                padding: '8px 12px',
                marginBottom: '5px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>{zone.name}:</strong> {formatOptimizationAction(zone.current, zone.optimization.target)}
                </div>
                <div style={{ color: '#90EE90', fontWeight: 'bold' }}>
                  Save ${zone.frictionAmount.toLocaleString()}/year
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Individual Readiness Zones */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
        {readinessZones.map((zone, index) => {
          const optimization = getOptimizationAction(zone);
          
          return (
            <div key={index} style={{
              padding: '15px',
              border: `2px solid ${zone.isInOptimal ? 'var(--ra-success)' : 'var(--ra-danger)'}`,
              borderRadius: '8px',
              backgroundColor: zone.isInOptimal ? 'rgba(0, 255, 136, 0.08)' : 'rgba(255, 68, 68, 0.08)',
              position: 'relative',
              minHeight: '120px' // Ensure enough space for all content
            }}>
              {/* Zone status icon in top-right */}
              <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '16px' }}>
                {getZoneIcon(zone)}
              </div>
              
              {/* Title and score in separate rows for better visibility */}
              <div style={{ marginBottom: '15px', paddingRight: '30px' }}>
                <h4 style={{ 
                  margin: 0, 
                  marginBottom: '8px',
                  color: zone.isInOptimal ? 'var(--ra-success)' : 'var(--ra-danger)',
                  fontSize: '16px'
                }}>
                  {zone.name}
                </h4>
                {/* Dual band display when simulating OR when scores differ */}
                {(isSimulating || (asIsScores && toBeScores)) && asIsScores && toBeScores ? (() => {
                  const scoreInfo = getScoreDisplayInfo(zone.name, zone.current);
                  const asIsBand = getReadinessBandLabel(scoreInfo.asIs);
                  const toBeBand = getReadinessBandLabel(scoreInfo.toBe);
                  const asIsStyle = getReadinessBandStyle(scoreInfo.asIs);
                  const toBeStyle = getReadinessBandStyle(scoreInfo.toBe);
                  const delta = Math.round((scoreInfo.toBe - scoreInfo.asIs) * 10) / 10;
                  const bandChanged = asIsBand !== toBeBand;

                  // No change at all — prompt to select recommendations
                  if (!scoreInfo.hasChange) {
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: asIsStyle.text,
                          backgroundColor: 'var(--ra-glass-bg)',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          border: `1px solid ${asIsStyle.border}`,
                        }}>
                          {asIsStyle.icon} {asIsBand}
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--ra-text-muted)', fontStyle: 'italic' }}>
                          Select recommendations to see projected impact
                        </div>
                      </div>
                    );
                  }

                  // Score changed — always show AS-IS → TO-BE with delta
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {/* As-Is Band */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: '10px', color: 'var(--ra-text-secondary)', marginBottom: '2px' }}>AS-IS</div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: asIsStyle.text,
                          backgroundColor: 'var(--ra-glass-bg)',
                          display: 'inline-block',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          border: `1px solid ${asIsStyle.border}`,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}>
                          {asIsStyle.icon} {asIsBand}
                        </div>
                      </div>

                      {/* Arrow */}
                      <div style={{ fontSize: '16px', color: 'var(--ra-cyber-aqua)' }}>→</div>

                      {/* To-Be Band */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: '10px', color: 'var(--ra-cyber-aqua)', marginBottom: '2px', fontWeight: 'bold' }}>TO-BE</div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: bandChanged ? toBeStyle.text : asIsStyle.text,
                          backgroundColor: bandChanged ? 'rgba(0, 102, 255, 0.08)' : 'var(--ra-glass-bg)',
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          border: bandChanged ? `2px solid ${toBeStyle.border}` : `1px solid ${asIsStyle.border}`,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}>
                          {toBeStyle.icon} {toBeBand}
                        </div>
                      </div>

                      {/* Delta indicator — shows direction even within the same band */}
                      <div style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: delta > 0 ? 'var(--ra-success)' : 'var(--ra-danger)',
                        backgroundColor: delta > 0 ? 'rgba(0, 255, 136, 0.08)' : 'rgba(255, 68, 68, 0.08)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        border: `1px solid ${delta > 0 ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 68, 68, 0.2)'}`,
                      }}>
                        {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}pts
                      </div>
                    </div>
                  );
                })() : (() => {
                  /* Single band display when not simulating */
                  const currentStyle = getReadinessBandStyle(zone.current);
                  return (
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: currentStyle.text,
                      backgroundColor: 'var(--ra-glass-bg)',
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      border: `1px solid ${currentStyle.border}`,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}>
                      {currentStyle.icon} {currentStyle.label}
                    </div>
                  );
                })()}
              </div>
              
              <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px' }}>
                <strong>Target:</strong> {getOptimalRangeLabel(zone.optimalMin, zone.optimalMax)}
                {zone.industryStandard && (
                  <div style={{ fontSize: '11px', color: 'var(--ra-text-muted)', marginTop: '2px' }}>
                    Industry: {getReadinessBandLabel(typeof zone.industryStandard === 'number' ? zone.industryStandard : zone.optimalMin)} • Best-in-Class: {getReadinessBandLabel(typeof zone.bestInClass === 'number' ? zone.bestInClass : zone.optimalMax)}
                  </div>
                )}
              </div>
              
              {/* Visual progress bar with optimal zone indicator */}
              <div style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '4px',
                position: 'relative',
                marginBottom: '8px'
              }}>
                {(() => {
                  // Calculate scale for raw scores (assume max range is 0-150 for most categories)
                  const maxScale = 150;
                  const currentPercent = Math.max(0, Math.min(100, (zone.current / maxScale) * 100));
                  const optimalMinPercent = Math.max(0, Math.min(100, (zone.optimalMin / maxScale) * 100));
                  const optimalMaxPercent = Math.max(0, Math.min(100, (zone.optimalMax / maxScale) * 100));
                  
                  return (
                    <>
                      {/* Optimal zone background */}
                      <div style={{
                        position: 'absolute',
                        left: `${Math.min(optimalMinPercent, optimalMaxPercent)}%`,
                        width: `${Math.abs(optimalMaxPercent - optimalMinPercent)}%`,
                        height: '100%',
                        backgroundColor: 'var(--ra-success)',
                        borderRadius: '4px',
                        opacity: 0.3
                      }} />
                      {/* Current position indicator */}
                      <div style={{
                        position: 'absolute',
                        left: `${currentPercent}%`,
                        width: '3px',
                        height: '100%',
                        backgroundColor: zone.isInOptimal ? 'var(--ra-success)' : 'var(--ra-danger)',
                        borderRadius: '1px'
                      }} />
                    </>
                  );
                })()}
              </div>
              
              {/* Friction cost with emphasis */}
              <div style={{ 
                fontSize: '12px', 
                fontWeight: 'bold',
                color: zone.isInOptimal ? 'var(--ra-success)' : 'var(--ra-danger)'
              }}>
                {zone.isInOptimal ? '✅ In Optimal Zone' : `❌ Friction Cost: ${zone.friction}`}
              </div>

              {/* Additional context for high-friction zones */}
              {zone.frictionAmount > 100000 && (
                <div style={{ 
                  marginTop: '8px',
                  fontSize: '11px',
                  color: 'var(--ra-text-secondary)',
                  fontStyle: 'italic'
                }}>
                  High-impact optimization opportunity
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary footer with business data context */}
      {businessData && (
        <div style={{ 
          marginTop: '20px',
          padding: '12px',
          backgroundColor: 'var(--ra-deep-blue)',
          borderRadius: '6px',
          fontSize: '11px',
          color: 'var(--ra-text-secondary)'
        }}>
          <strong>Revenue Context:</strong> Friction costs scaled for {String(businessData.actualRevenue?.formatted || 'Unknown')} company revenue | 
          <strong> Assessment:</strong> {String(businessData.assessmentId)} | 
          <strong> Source:</strong> Real Business Intelligence Data
        </div>
      )}
    </div>
  );
};

export default GoldilocksZones;